import { z } from 'zod';

import { CashConfigProvenanceSchema } from './cash-locations.schema.js';
import type {
  CashFundingSourceCategoryDto,
  CashFundingSourceSnapshotDto,
  CreateCashFundingSourceCategoryRequest,
  CreateCashFundingSourceCategoryResponse,
  DisableCashFundingSourceCategoryRequest,
  DisableCashFundingSourceCategoryResponse,
  ListCashFundingSourceCategoriesRequest,
  ListCashFundingSourceCategoriesResponse,
} from './cash-funding-source-categories.dto.js';

const timestampSchema = z.iso.datetime();
const optionalBooleanQuerySchema = z.preprocess((value) => {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true' || normalized === '1') {
      return true;
    }
    if (normalized === 'false' || normalized === '0') {
      return false;
    }
  }

  return value;
}, z.boolean().optional());

export const CashFundingSourceCategoryCodeSchema = z
  .string()
  .trim()
  .min(1)
  .max(80)
  .regex(/^[a-z][a-z0-9_]*$/);

export const CashFundingSourceCategoryDisplayNameSchema = z.string().trim().min(1).max(120);
export const CashFundingSourceCategoryDescriptionSchema = z.string().trim().min(1).max(500);

export const CashFundingSourceCategoryDtoSchema: z.ZodType<CashFundingSourceCategoryDto> = z.object(
  {
    id: z.uuid(),
    companyId: z.uuid(),
    code: CashFundingSourceCategoryCodeSchema,
    displayName: CashFundingSourceCategoryDisplayNameSchema,
    description: CashFundingSourceCategoryDescriptionSchema.nullable(),
    isActive: z.boolean(),
    sortOrder: z.number().int().nonnegative(),
    isBuiltIn: z.boolean(),
    provenance: CashConfigProvenanceSchema,
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  },
);

export const CashFundingSourceSnapshotDtoSchema: z.ZodType<CashFundingSourceSnapshotDto> = z.object(
  {
    id: z.uuid(),
    code: CashFundingSourceCategoryCodeSchema,
    displayName: CashFundingSourceCategoryDisplayNameSchema,
  },
);

export const ListCashFundingSourceCategoriesRequestSchema: z.ZodType<ListCashFundingSourceCategoriesRequest> =
  z.object({
    companyId: z.uuid(),
    activeOnly: optionalBooleanQuerySchema,
  });

export const ListCashFundingSourceCategoriesResponseSchema: z.ZodType<ListCashFundingSourceCategoriesResponse> =
  z.object({
    items: z.array(CashFundingSourceCategoryDtoSchema),
  });

export const CreateCashFundingSourceCategoryRequestSchema: z.ZodType<CreateCashFundingSourceCategoryRequest> =
  z.object({
    companyId: z.uuid(),
    operationId: z.uuid(),
    occurredAt: timestampSchema.optional(),
    code: CashFundingSourceCategoryCodeSchema.optional(),
    displayName: CashFundingSourceCategoryDisplayNameSchema,
    description: CashFundingSourceCategoryDescriptionSchema.nullable().optional(),
  });

export const CreateCashFundingSourceCategoryResponseSchema: z.ZodType<CreateCashFundingSourceCategoryResponse> =
  z.object({
    cashFundingSourceCategory: CashFundingSourceCategoryDtoSchema,
  });

export const DisableCashFundingSourceCategoryRequestSchema: z.ZodType<DisableCashFundingSourceCategoryRequest> =
  z.object({
    companyId: z.uuid(),
    categoryId: z.uuid(),
    operationId: z.uuid(),
    occurredAt: timestampSchema.optional(),
  });

export const DisableCashFundingSourceCategoryResponseSchema: z.ZodType<DisableCashFundingSourceCategoryResponse> =
  z.object({
    cashFundingSourceCategory: CashFundingSourceCategoryDtoSchema,
  });
